//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import styled from 'styled-components';

import { SecondaryButton } from '@components/atoms/button/Button';
import { ImageWithErrorHandling } from '@components/atoms/image-with-error-handling/ImageWithErrorHandling';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { SectionText } from '@components/atoms/text/Text';
import Cuid from '@models/Cuid';
import {
  UIATrackingIds,
  UIA_CUSTOM_COVER_IMAGE_MAX_WIDTH,
  UIA_CUSTOM_COVER_SELECTION_PREVIEW_ASPECT_RATIO_PORTRAIT,
} from '@uia/constants/uiaConstants';
import { UIACustomCoverImageSelectionModal } from '@uia/custom-cover-image/UIACustomCoverImageSelectionModal';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import { isDefined } from '@utils/filterPredicates';
import { getMedsSizedImageUrl } from '@utils/meds';

export interface UIACustomCoverImageProps {
  cuid: Cuid;
}

export const UIACustomCoverImage = ({ cuid }: UIACustomCoverImageProps) => {
  const { data: metadata, isError, isLoading } = useMediaMetadataQuery(cuid);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const videoUrl = metadata?.videoSource;

  return (
    <CoverImageWrapper>
      <SectionText>Gallery Cover Image</SectionText>
      {isError && (
        <div>
          An unexpected error occurred. Please try again or contact Bazaarvoice
          Support.
        </div>
      )}
      {isLoading ? (
        <CenteredSpinner />
      ) : (
        <>
          {isDefined(metadata) && (
            <CoverImageContainer>
              <Image
                alt='Cover Image'
                src={
                  metadata.customThumbnailFileId
                    ? getMedsSizedImageUrl(metadata.customThumbnailFileId, 'l')
                    : metadata.thumbnailUrl
                }
              />
              <EditCoverButtonWrapper>
                <SecondaryButton
                  onClick={() => {
                    setIsModalOpen(true);
                  }}
                  trackingId={UIATrackingIds.COVER_IMAGE_EDIT_BUTTON}
                >
                  Edit Cover Image
                </SecondaryButton>
              </EditCoverButtonWrapper>
              <UIACustomCoverImageSelectionModal
                coverImageFileId={metadata.customThumbnailFileId}
                cuid={cuid}
                durationMillis={metadata.video?.durationMillis}
                fileId={metadata.fileId}
                hasCustomThumbnailOffset={isDefined(
                  metadata.customThumbnailOffsetMilliseconds,
                )}
                isBrandOwned={metadata.isBrandOwned}
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                url={videoUrl}
                videoThumbnailOffsetSeconds={
                  metadata.customThumbnailOffsetMilliseconds
                    ? metadata.customThumbnailOffsetMilliseconds / 1000
                    : 0
                }
              />
            </CoverImageContainer>
          )}
        </>
      )}
    </CoverImageWrapper>
  );
};

const CoverImageWrapper = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[3]};
  `,
);

const CoverImageContainer = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: ${theme.spacings[3]};
 `,
);

const Image = styled(ImageWithErrorHandling)`
  max-width: ${UIA_CUSTOM_COVER_IMAGE_MAX_WIDTH}px;
  aspect-ratio: ${UIA_CUSTOM_COVER_SELECTION_PREVIEW_ASPECT_RATIO_PORTRAIT};
  object-fit: cover;
`;

const EditCoverButtonWrapper = styled.div(
  ({ theme }) => `
    text-align: center;
    margin-top: ${theme.spacings[3]};
  `,
);
